<script lang="ts">
  /**
   * NEWS — the liquidity-context rail (plan §13, second row beside the tide).
   *
   * ============================================================================
   * ⚖ CAPSULE §16, VERBATIM: *NEWS NEVER FIRES A TRADE — IT CLASSIFIES LIQUIDITY.*
   * This panel renders headline / source / MAJOR flag / age / relevance tag and NOTHING
   * else. No sentiment colouring, no scores, no arrows, no levels — a rail that renders as
   * a signal feed breaches LAW 5 (detection ≠ action). The server sends `sentiment`; this
   * panel deliberately does not paint it, because a green headline IS a signal to a reader
   * scanning at a glance, whatever the footer says.
   * ============================================================================
   *
   * THE THREE HONEST ABSENCES stay distinct all the way to the screen (lib/newsFeed.ts):
   *   · 404-before-T  → "NO SNAPSHOT" block, server's reason VERBATIM (capture-lane fact)
   *   · EMPTY         → quiet note naming the filter and the LIVE snapshot behind it
   *   · STALE         → StalenessChip goes LOUD + the list dims (SPX-044 clause (4)'s
   *                     render half — the arms in newsFeed.test.ts drive this)
   *
   * UW HALF ONLY: the route says so in its BODY (`union_missing: ["BABEL"]`) and the
   * caveat here is DERIVED from those fields, so it self-retires the day the union lands
   * (D-3 precedent) instead of surviving as a hardcoded lie.
  */
  import { onMount } from "svelte";
  import { fetchNews, formatAge, type FeedError } from "./api";
  import StalenessChip from "./StalenessChip.svelte";
  import PanelInfo from "./PanelInfo.svelte";
  import AgePill from "./AgePill.svelte";
  import { keptPayloadAfterFailure } from "./dataAge";
  import { reportDoorFailure, type DoorFaultEvidence } from "./faultEvidence";
  import {
    classifyNewsFeed,
    newsChipInputs,
    newsYieldClaim,
    newsStaleShaped,
    newsItemKey,
    newsStale,
    unionCaveat,
    headlineAgeS,
    severityChip,
    severityAttribution,
    NEWS_FALLBACK_BUDGET_S,
    type NewsResponse,
    type FeedFailure,
  } from "./newsFeed";

  interface Props {
    /**
     * The route's default and the desk's: index_or_macro. NO yield figure is quoted here:
     * the rate is day-class dependent and lives on the payload (`measured_yield_applicable`).
     * A number in a comment is a claim that cannot be re-measured when the world moves —
     * this one said 19.7 while the route served 1.25.
     */
    relevance?: string;
    limit?: number;
    /** ~60s: the budget is 120s, so two polls fit inside one budget window. */
    pollMs?: number;
    /** Workspace registration (workspace.ts `collapsed: ["news", …]`). Re-stamped on switch. */
    startCollapsed?: boolean;
    /**
     * Fired when the READER folds this panel — never when the workspace re-stamps it.
     * ⚠ THE DISTINCTION IS THE WHOLE FEATURE: persisting a re-stamp would write the default
     * back as if it were a choice, and the reader could then never return to "no preference".
     */
    onToggle?: (collapsed: boolean) => void;
  }
  let {
    relevance = "index_or_macro", limit = 50, pollMs = 60_000, startCollapsed = false, onToggle,
  }: Props = $props();

  let payload = $state<NewsResponse | null>(null);
  let failure = $state<FeedFailure | null>(null);
  let fault = $state<DoorFaultEvidence | null>(null);
  let loading = $state(true);
  /** Wall-clock at which `payload` arrived — the served age_s is frozen at serve time. */
  let fetchedAtMs = $state<number | null>(null);
  let bodyRequestKey = $state<string | null>(null);
  /** Re-stamped every 15s so headline ages and the chip's clock advance between polls. */
  let nowMs = $state(Date.now());
  let collapsed = $state(false);
  let infoOpen = $state(false);
  // The workspace stamps its choice on switch; a manual toggle wins until the next switch
  // (same semantics as workspace → theme in App.svelte).
  $effect(() => {
    collapsed = startCollapsed;
  });

  let classified = $derived(classifyNewsFeed(payload, failure));
  let verdict = $derived(classified);
  let elapsedS = $derived(fetchedAtMs === null ? 0 : Math.max(0, (nowMs - fetchedAtMs) / 1000));
  /**
   * ⛔ CLAUSE (4) WIRING — the chip reads the ROUTE'S OWN age_s/budget_s (plus honest
   * elapsed drift) and gets NO `lastBarMs`: news has no market session, so the CLOSED
   * escape hatch must be unreachable and over-budget renders exactly one way — LOUD.
   */
  let chip = $derived(payload === null ? null : newsChipInputs(payload, elapsedS));
  /**
   * ⛔ P2 ③ (driven by HUDCHAT-s 2026-08-15): this read `newsStaleShaped(payload)` alone, which
   * uses the payload's FROZEN served age and ignores locally-elapsed drift entirely — while the
   * chip beside it adds that drift. Driven: age 100 of a 120 budget + 90s local drift ⇒
   * ** the chip rendered STALE while every headline stayed fully opaque. **
   * ⇒ *** TWO SURFACES ON ONE PANEL DISAGREEING ABOUT THE SAME FEED IS WORSE THAN EITHER BEING
   *   WRONG ALONE: the chip says do not trust this and the list says it is fine. ***
   * The dimming now keys on the SAME over-budget test the chip does, so they cannot diverge.
   */
  let stale = $derived(payload !== null && newsStale(payload, elapsedS));

  let caveat = $derived(payload === null ? null : unionCaveat(payload));

  async function load() {
    const requestKey = `/api/es/news?relevance=${encodeURIComponent(relevance)}&limit=${limit}`;
    try {
      payload = await fetchNews(relevance, limit);
      failure = null;
      fault = null;
      fetchedAtMs = Date.now();
      bodyRequestKey = requestKey;
      nowMs = Date.now();
    } catch (err) {
      // Fail LOUD (law 1). The last good payload stays on screen DIMMED with its banner —
      // the dealer rail's pattern — never rendered as if nothing happened.
      const fe = err as FeedError;
      nowMs = Date.now();
      fault = reportDoorFailure("news", err, fetchedAtMs, nowMs);
      failure = {
        status: typeof fe?.status === "number" ? fe.status : 0,
        message: fe?.message ?? String(err),
        detail: fe?.detail ?? null,
      };
      const body = payload;
      payload = keptPayloadAfterFailure(body, bodyRequestKey, requestKey);
      if (payload === null) {
        fetchedAtMs = null;
        bodyRequestKey = null;
      }
    } finally {
      loading = false;
    }
  }

  onMount(() => {
    void load();
    const poll = setInterval(() => void load(), pollMs);
    const clock = setInterval(() => (nowMs = Date.now()), 15_000);
    return () => {
      clearInterval(poll);
      clearInterval(clock);
    };
  });

  const age = (createdAt: string): string => {
    const s = headlineAgeS(createdAt, nowMs);
    // A headline that cannot date itself gets an em-dash, never a fabricated age.
    return s === null ? "—" : formatAge(s);
  };
</script>

<!-- `news` is this panel's ID in the vocabulary workspace.ts already registers
     (`collapsed: ["news", "flow"]`) and App.svelte's layout targets — same convention
     as `.card.tide`. -->
<section class="card news">
  <header class="panel-id">
    <h2>NEWS</h2>
    <span class="pill">{payload === null ? "—" : payload.count}</span>
    <button
      class="fold"
      onclick={() => {
        collapsed = !collapsed;
        onToggle?.(collapsed);
      }}
      aria-expanded={!collapsed}
    >
      {collapsed ? "▸" : "▾"}
    </button>
    <StalenessChip
      label="news"
      pollKey={payload}
      ageS={chip?.ageS ?? null}
      budgetS={chip?.budgetS ?? NEWS_FALLBACK_BUDGET_S}
      error={failure !== null && failure.status !== 404 ? failure.message : null}
      compactLive={true}
      barSpanMs={0}
    />
    <AgePill payload={payload} {fetchedAtMs} door={bodyRequestKey ?? `/api/es/news?relevance=${encodeURIComponent(relevance)}&limit=${limit}`} />
    <PanelInfo id="news-info" label="News information" open={infoOpen} ontoggle={() => (infoOpen = !infoOpen)}>
      <p>{payload?.root ?? "—"} · liquidity context · {payload?.relevance_mode ?? relevance}</p>
      <p>{caveat ?? "UW half only — BABEL absent — this rail is partial by construction"}</p>
      <p>0 headlines pass the index_or_macro filter — a quiet window, not a fault.</p>
      <p>NEWS NEVER FIRES A TRADE — IT CLASSIFIES LIQUIDITY (capsule §16).</p>
      {#if payload}
        {@const claim = newsYieldClaim(payload)}
        {#if claim}
          <p>This filter yields ~{claim.perDay}/day on a {claim.dayClass} day.</p>
          <p>measured over {claim.window}</p>
        {:else}
          <p>NO MEASURED YIELD for this day-class — the route serves no applicable figure.</p>
        {/if}
        {#if verdict?.kind === "empty" && verdict.payload.measured_yield_basis}
          <p>{verdict.payload.measured_yield_basis}</p>
        {/if}
      {:else}
        <p>measured over the served study window</p>
      {/if}
      {#if payload}<p>{payload.count} headline{payload.count === 1 ? "" : "s"} before {payload.as_of.slice(11, 19)}Z</p>{/if}
      {#if payload && payload.dropped_items > 0}<p>{payload.dropped_items} unplaceable rows dropped server-side</p>{/if}
      {#if payload}<p>{severityAttribution(payload.severity_source)}</p>{/if}
    </PanelInfo>
  </header>

  {#if !collapsed}
    <div class="body">
      {#if fault}
        <div class="loud" role="alert">
          <b>DOOR FAILURE — this rail is not showing live news</b>
          <span>{fault.label}</span>
          {#if fault.retained}<span>{fault.retained}</span>{/if}
          <button onclick={load}>Retry</button>
        </div>
      {/if}
      {#if verdict === null}
        {#if loading}<p class="loading" aria-label="loading news">●</p>{/if}
      {:else if verdict.kind === "no-snapshot"}
        <!-- The route's 404: a fact about OUR CAPTURE LANE, rendered verbatim. NOT an empty
             list (that claims a quiet news day) and NOT a generic error (the route answered). -->
        <div class="absent" role="note">
          <b>NO SNAPSHOT</b>
        </div>
      {:else if verdict.kind === "error"}
        <div class="loud" role="alert"><b>{verdict.message}</b></div>
      {/if}

      {#if verdict?.kind === "empty"}
        <!--
          The snapshot EXISTS — that is what separates this from the 404 above.

          ⛔ THE NUMBER HERE USED TO BE A WEEKDAY BLEND QUOTED AT A WEEKEND. Measured on the
          served route: the BLENDED per-day map served a weekday-blended rate on a SUNDAY
          whose applicable yield is 1.25. So this rail told the owner "0 items is normal, this
          filter yields ~19.7/day" — a sentence that explains an empty SUNDAY with a WEEKDAY
          rate. yy measured the split (weekday 26.62 · weekend 1.25); 19.7 is right on NO day.
          ⇒ ***A YIELD WITHOUT ITS DAY-CLASS IS A NUMBER LOOKING FOR A DAY THAT DOES NOT EXIST.***

          Now it renders the APPLICABLE yield and yy's basis VERBATIM — filter, day-class and
          measurement window in the route's own words, the `union_note` precedent this rail
          already follows. A summary of a disclosure is a new claim nobody keyed.
          ⚠ AND NO `?? 19.7`: when the route serves no applicable yield the panel SAYS SO. The
          old fallback would have fabricated a number the moment the field went missing, and a
          fabricated normal is worse than an admitted unknown.
        -->
        <div class="empty"><b>NO HEADLINES</b></div>
      {:else if verdict?.kind === "present"}
        <!-- SERVED ORDER, deliberately un-re-sorted: the route sorts newest-first
             (news.ts filterHeadlines) and a client-side re-sort would silently paper over a
             server that stopped honouring its own contract. -->
        <ol class="list" class:dimmed={stale || failure !== null}>
          {#each verdict.payload.items as item (newsItemKey(item))}
            <!-- OURS, not the vendor's. The chip carries its own basis in the title and the
                 rail states the attribution once below (severityAttribution) — a bare score
                 beside vendor headlines is the `CVD (session)` mislabel with extra steps.
                 `{@const}` must be an immediate child of the `{#each}`, not of the <li>. -->
            {@const sev = severityChip(item)}
            <li class="row">
              <span class="hlage num" title={item.created_at}>{age(item.created_at)}</span>
              <span class="src">{item.source || "—"}</span>
              {#if item.is_major}<span class="major">MAJOR</span>{/if}
              {#if sev}<span class="sev sev-{sev.band}" title={sev.title}>{sev.band} {sev.score}</span>{/if}
              <span class="hl">{item.headline}</span>
            </li>
          {/each}
        </ol>
      {/if}
    </div>
  {/if}
</section>

<style>
  .card {
    background: var(--surface);
    border: 1px solid var(--line);
    border-radius: var(--radius-card);
    box-shadow: var(--shadow);
    display: flex;
    flex-direction: column;
    min-width: 0;
  }
  .hd {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    border-bottom: 1px solid var(--line);
    flex-wrap: wrap;
  }
  .hd h2 {
    margin: 0;
    font-size: 12px;
    letter-spacing: 0.12em;
    font-weight: 700;
  }
  .sub { color: var(--muted); font-size: 11px; }
  .sub b { color: var(--ink); font-weight: 600; }
  .fold {
    border: 1px solid var(--line);
    background: transparent;
    color: var(--muted);
    border-radius: 5px;
    font-size: 10px;
    padding: 1px 7px;
    cursor: pointer;
  }
  .fold:hover { background: var(--chip); }

  .body { padding: 10px 14px 12px; min-width: 0; }

  .caveat {
    margin: 0 0 10px;
    font-size: 11px;
    line-height: 1.45;
    color: var(--muted);
    border-left: 1px solid var(--stale);
    padding-left: 10px;
  }
  .caveat b { color: var(--stale-ink); }

  /* NO SNAPSHOT: bordered and labelled — it can never read as a quiet news day — but not
     amber: the route ANSWERED, and honestly. Amber is for faults and stale data. */
  .absent {
    display: flex;
    flex-direction: column;
    gap: 4px;
    border: 1px dashed var(--line);
    background: var(--surface-2);
    color: var(--muted);
    border-radius: var(--radius-chip);
    padding: 10px 12px;
    font-size: 12px;
    margin-bottom: 10px;
  }
  .absent b { color: var(--ink); font-size: 11px; letter-spacing: 0.06em; }

  .loud {
    display: flex;
    flex-direction: column;
    gap: 4px;
    align-items: flex-start;
    border: 1px solid var(--stale);
    background: var(--stale-soft);
    color: var(--stale-ink);
    border-radius: var(--radius-chip);
    padding: 10px 12px;
    font-size: 12px;
    margin-bottom: 10px;
  }
  .loud span { color: var(--muted); font-size: 11px; }
  .loud button {
    margin-top: 4px;
    background: var(--surface-2);
    border: 1px solid var(--line);
    color: var(--ink);
    border-radius: 6px;
    padding: 3px 10px;
    font-size: 11px;
    cursor: pointer;
  }
  .loud button:hover { background: var(--chip); }

  .muted { color: var(--muted); font-size: 12px; margin: 0; }
  .empty { color: var(--muted); font-size: 12px; line-height: 1.5; margin: 0 0 8px; }
  /* Provenance for the yield figure beside it. Muted: it is the AUDIT trail, not the
     claim — but present on the surface, because a rate with no window is unfalsifiable. */
  .basis {
    display: block;
    margin-top: 2px;
    font-size: 10px;
    /* Token only, no literal fallback: `--muted` IS defined in tokens.css, so the
       fallback could never fire — it was dead code that still failed LAW 8, and a
       hardcoded colour that never renders is still a hardcoded colour. (w, R25) */
    color: var(--muted);
    font-variant-numeric: tabular-nums;
  }
  .empty b { color: var(--ink); font-weight: 600; }

  .list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    transition: opacity 0.2s ease;
  }
  .dimmed { opacity: 0.55; }
  .row {
    display: grid;
    grid-template-columns: auto auto auto 1fr;
    gap: 8px;
    align-items: baseline;
    padding: 5px 0;
    border-bottom: 1px solid var(--line);
    font-size: 12px;
  }
  .row:last-child { border-bottom: 0; }
  .hlage {
    font-family: var(--font-num);
    font-size: 10px;
    color: var(--faint);
    white-space: nowrap;
    min-width: 44px;
  }
  .src {
    font-size: 9px;
    letter-spacing: 0.06em;
    color: var(--muted);
    text-transform: uppercase;
    white-space: nowrap;
  }
  .major {
    font-size: 9px;
    letter-spacing: 0.08em;
    font-weight: 700;
    color: var(--ink);
    background: var(--chip);
    border: 1px solid var(--line);
    border-radius: 4px;
    padding: 0 5px;
  }
  /* OURS-computed severity. Deliberately QUIETER than .major: MAJOR is the vendor's own
     flag, this is our stopgap score, and the visual weight must not claim more authority
     than the provenance does. Tokens only (LAW 8) — both themes come from the same vars. */
  .sev {
    font-size: 9px;
    letter-spacing: 0.06em;
    font-weight: 600;
    color: var(--muted);
    background: transparent;
    border: 1px dashed var(--line);
    border-radius: 4px;
    padding: 0 5px;
    white-space: nowrap;
    text-transform: uppercase;
  }
  /* A band we have HEARD of reads at full ink; an unheard one keeps the muted default
     rather than being styled as if we understood it. */
  .sev-high { color: var(--ink); border-style: solid; }
  .sev-medium { color: var(--ink); }
  .attrib {
    margin: 6px 0 0;
    font-size: 11px;
    color: var(--muted);
    font-style: italic;
  }
  .hl { color: var(--ink); line-height: 1.4; min-width: 0; overflow-wrap: anywhere; }

  .num { font-family: var(--font-num); }

  .foot {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid var(--line);
  }
  .foot .muted { font-size: 10px; }

  .law {
    margin: 10px 0 0;
    font-size: 9px;
    letter-spacing: 0.08em;
    color: var(--faint);
  }
</style>
